// ── Porta do servidor da Lista de Vidas ───────────────────────────
// Contrato HTTP: POST /api/lista-vidas { acao, params }. O servidor é quem lê
// e escreve no Hub; o navegador só vê DTOs e nunca recebe chave do Hub.
// `acoes` (da solicitação) e `operacoesPossiveis` (da vida) vêm calculados
// pelo servidor: a tela combina com o papel, não recalcula permissão.

import type {
  AcaoSolicitacao, AvisoCodigo, MotivoConflito, Papel, PedidoSolicitacao,
  SolicitacaoStatus,
} from '../solicitacaoModel';
import type { TipoOperacao } from '../vidaHubModel';
import type {
  AdicionalEmpresa, AdicionalVida, BonificacaoEmpresa, ControleAdicional, LiberacaoZenklub,
  ResumoAdicionais, StatusAdicional,
} from '../adicionaisModel';

export type { CamposAdicao } from '../solicitacaoModel';

/** Liberação como o servidor devolve: além do `liberadoPor` (id), o e-mail de
 *  quem liberou, já resolvido (pode faltar se o usuário não existir mais). */
export interface LiberacaoZenklubDTO extends LiberacaoZenklub {
  liberadoPorEmail?: string | null;
}

export interface ControleAdicionalDTO extends Omit<ControleAdicional, 'liberacao'> {
  liberacao: LiberacaoZenklubDTO | null;
}

/** Controle por vida; `null` quando a vaga não é elegível (cancelada ou sem pessoa). */
export type ControleAdicionaisDTO = Record<AdicionalVida, ControleAdicionalDTO | null>;

export interface AdicionaisEmpresaDTO {
  resumo: ResumoAdicionais;
  bonificacoes: Record<AdicionalEmpresa, BonificacaoEmpresa>;
}

export interface ListaVidasPapelInfo {
  papel: Papel | null;
  configurado: boolean;
  leaseSegundos: number;
  limites24h: Record<TipoOperacao, number>;
}

export interface EmpresaHubDTO {
  empresaPublicId: string;
  razaoSocial: string | null;
  cnpj: string | null;
  marca: 'bluehub' | 'edp' | 'tfo';
  status: string | null;
  vidasAtivas: number;
  vidasVendidas: number;
  /** Existe cs.hub_contas com o mesmo hub_public_id. */
  criadaPeloCrm: boolean;
  /** Solicitações abertas da empresa. */
  abertas: number;
}

/** Uma empresa na visão geral (sem dado pessoal: só contagens). Com `erro`, a
 *  leitura do Hub falhou e os números vêm null. */
export interface VisaoGeralEmpresaDTO extends EmpresaHubDTO {
  vagas: number | null;
  ocupadas: number | null;
  livres: number | null;
  adicionais: ResumoAdicionais | null;
  /** Adicionais com bonificação que ainda vale (fim hoje ou depois). */
  bonificados: AdicionalEmpresa[];
  lidoEm: string | null;
  erro: string | null;
}

export interface ListaVidasVisaoGeral {
  empresas: VisaoGeralEmpresaDTO[];
  totais: {
    empresas: number; comErro: number; vagas: number; ocupadas: number; livres: number;
    totalpass: number; odonto: number; psicologo: number; psicologoAtivos: number; abertas: number;
  };
}

export interface ListaVidasEmpresas {
  empresas: EmpresaHubDTO[];
  sincronizadoEm: string | null;
}

export interface VidaResumoDTO {
  seatPublicId: string;
  nome: string | null;
  email: string | null;
  telefone: string | null;
  cpfMascarado: string | null;
  sexo: 'M' | 'F' | null;
  sexoOrigem: 'informado' | 'inferido_pelo_nome' | null;
  plano: { nome: string | null; slug: string | null; interval: string | null } | null;
  adicionais: string[];
  seatStatus: string;
  ativo: boolean;
  tipo: 'titular' | 'dependente' | null;
  titularSeatPublicId: string | null;
  parentesco: string | null;
  temPessoa: boolean;
  temCpf: boolean;
  temDataNascimento: boolean;
  temNomeDaMae: boolean;
  qtdDependentes: number;
  solicitacaoAbertaId: string | null;
  operacoesPossiveis: TipoOperacao[];
  /** Opcional só por compatibilidade com servidor antigo; o atual sempre manda. */
  controleAdicionais?: ControleAdicionaisDTO;
}

/** PII completa: só em `obter-vida` (preencher a edição), com log de acesso. */
export interface VidaCompletaDTO extends VidaResumoDTO {
  cpf: string | null;
  dataNascimento: string | null;
  nomeDaMae: string | null;
}

export interface ListaVidasVidas {
  empresa: EmpresaHubDTO;
  total: number;
  vidas: VidaResumoDTO[];
  livresPorPlano: Record<string, number>;
  planosVistos: { slug: string; nome: string | null }[];
  /** slug -> solicitacaoId */
  abertaRemoverPorPlano: Record<string, string>;
  /** Aplicadas por ESTA tela nas últimas 24h. */
  uso24h: Record<TipoOperacao, number>;
  cpfVisivel: boolean;
  lidoEm: string;
  /** Resumo e bonificações por adicional da empresa (F1: só no CRM). */
  adicionaisEmpresa?: AdicionaisEmpresaDTO;
}

export interface SalvarAdicionalVidaInput {
  empresaPublicId: string;
  seatPublicId: string;
  adicional: AdicionalVida;
  status: StatusAdicional;
  /** Versão lida (CAS); `null` = ainda não há linha no CRM. */
  versao: number | null;
}

export interface LiberarZenklubInput {
  empresaPublicId: string;
  seatPublicId: string;
  versao: number | null;
  pedidoEm: string;
  gestorRhNome: string;
  /** Padrão: hoje (o servidor completa). */
  liberadoEm?: string;
}

export interface SalvarBonificacaoInput {
  empresaPublicId: string;
  adicional: AdicionalEmpresa;
  bonificado: boolean;
  inicio?: string | null;
  fim?: string | null;
  versao: number | null;
}

/** O pedido do CS, com o id gerado ao abrir o diálogo e reaproveitado no retry. */
export type NovaSolicitacao = PedidoSolicitacao & { clienteId: string };

export interface RespostaHubDTO {
  correlationId: string | null;
  jaExistia: boolean | null;
  camposAlterados?: string[];
  situacao?: 'convite_pendente' | 'com_acesso';
  sessoesEncerradas?: boolean;
  notificacaoEmail?: string | null;
  desfecho?: 'cadastrada' | 'atualizada' | 'sem_mudanca';
  naoAplicado?: string[];
  vagasCriadas?: number;
  totalVagas?: number;
  mensalidadeCents?: number;
  usuarioPublicId?: string;
  declaracaoDeVinculo?: boolean;
  vagasAntes?: number;
  vagasDepois?: number;
  mensalidadeAntesCents?: number;
  mensalidadeDepoisCents?: number;
}

/** `erro.codigo` gravado na solicitação (tabela 1.7 + adendo D4/D5). Enum
 *  ABERTO: `ErroRegistradoDTO.codigo` continua `string` e a tela tem texto
 *  genérico para o que não conhece. */
export type CodigoErroRegistrado =
  | 'validacao_local' | 'nao_configurado' | 'rede' | 'em_andamento' | 'chave_queimada'
  | 'conta_encerrada' | 'acesso_retido' | 'teto' | 'indisponivel' | 'credencial' | 'escopo'
  | 'nao_encontrado' | 'validacao' | 'recusa_negocio' | 'requisicao_invalida' | 'provedor_502' | 'hub_5xx'
  | 'leitura_falhou' | 'leitura_404' | 'lease_esgotado'
  | 'interno' | 'hub_indisponivel' | 'leitura_incompleta' | 'leitura_sem_cpf' | 'chave_reutilizada_remocao';

/** `codigo` das respostas de erro HTTP (contrato 3.3 + adendo D5). `rede`
 *  (status 0) é do cliente: a resposta não chegou. */
export type CodigoErroHttp =
  | 'json_invalido' | 'acao_desconhecida' | 'parametro_invalido'
  | 'nao_autenticado' | 'sessao_invalida'
  | 'sem_papel' | 'papel_insuficiente' | 'sem_permissao' | 'auto_aprovacao'
  | 'solicitacao_nao_encontrada' | 'empresa_nao_encontrada' | 'vida_nao_encontrada' | 'empresa_fora_do_alcance'
  | 'transicao_invalida' | 'lease_ativo' | 'solicitacao_aberta' | 'cliente_id_em_uso' | 'resultado_incerto'
  | 'ja_gravada' | 'leitura_incompleta'
  | 'validacao' | 'versao_desatualizada' | 'motivo_obrigatorio' | 'motivo_longo' | 'relogio_obrigatorio'
  | 'muitas_solicitacoes' | 'muitas_consultas'
  | 'hub_indisponivel' | 'hub_leitura_recusada'
  | 'nao_configurado' | 'hub_nao_autorizado' | 'leitura_sem_cpf' | 'conclusao_pendente' | 'auth_indisponivel'
  | 'interno' | 'rede';

export interface ErroRegistradoDTO {
  codigo: string;
  httpStatus: number | null;
  hubCode: string | null;
  correlationId: string | null;
  retryAfterS?: number;
  campos?: string[];
}

export interface ConflitoDTO {
  motivo: MotivoConflito;
  campos: string[];
  seatStatusAtual?: string;
}

export interface SolicitacaoDTO {
  id: string;
  tipo: TipoOperacao;
  status: SolicitacaoStatus;
  empresaPublicId: string;
  empresaNome: string | null;
  seatPublicId: string | null;
  alvoTipo: 'seat' | 'cpf' | 'plano';
  antes: Record<string, unknown> | null;
  depois: Record<string, unknown>;
  avisos: AvisoCodigo[];
  justificativa: string | null;
  solicitanteId: string;
  solicitadoPorEmail: string | null;
  decididoPor: string | null;
  decididoEm: string | null;
  motivoRejeicao: string | null;
  aceitouIncerteza: boolean;
  aplicandoDesde: string | null;
  tentativas: number;
  escritaIncerta: boolean;
  aplicadaEm: string | null;
  jaAplicada: boolean | null;
  respostaHub: RespostaHubDTO | null;
  conflitoAtual: ConflitoDTO | null;
  erro: ErroRegistradoDTO | null;
  piiExpurgada: boolean;
  /** true em `listar-solicitacoes`: `antes`/`depois` vêm por `mascararPii`
   *  (CPF e telefone mascarados; sem nascimento e nome da mãe). Em
   *  `obter-solicitacao` vem ausente/false: PII completa enquanto aberta
   *  (adendo D4). */
  piiMascarada?: boolean;
  createdAt: string;
  acoes: AcaoSolicitacao[];
}

export interface FiltroSolicitacoes {
  status?: SolicitacaoStatus[];
  tipo?: TipoOperacao[];
  empresaPublicId?: string;
  minhas?: boolean;
  /** 1..200, padrão 50. */
  limite?: number;
  /** ISO: paginação para trás. */
  antesDe?: string;
}

export interface ListaVidasSolicitacoes {
  solicitacoes: SolicitacaoDTO[];
  servidorAgora: string;
}

export interface EventoSolicitacao {
  evento: string;
  ator: string | null;
  dados: Record<string, unknown>;
  createdAt: string;
}

export interface DetalheSolicitacao {
  solicitacao: SolicitacaoDTO;
  eventos: EventoSolicitacao[];
}

export type ResultadoAplicacao = 'aplicada' | 'ja_aplicada' | 'conflito' | 'falha' | 'recusada';

export interface RespostaAplicacao {
  solicitacao: SolicitacaoDTO;
  resultado: ResultadoAplicacao;
  conflito?: ConflitoDTO;
  erro?: ErroRegistradoDTO;
}

/** Erro tipado. `codigo: 'rede'` (status 0) = a resposta nunca chegou: o
 *  efeito no servidor é desconhecido, então a tela deve reler. */
export class ListaVidasGatewayError extends Error {
  readonly codigo: string;
  readonly status: number;
  readonly mensagem: string;
  readonly erros?: unknown[];
  readonly solicitacaoId?: string;
  readonly statusAtual?: string;
  readonly retentavel?: boolean;
  readonly correlationId?: string;
  readonly retryAfterS?: number;

  constructor(input: {
    codigo: string; status: number; mensagem: string;
    erros?: unknown[]; solicitacaoId?: string; statusAtual?: string; retentavel?: boolean;
    correlationId?: string; retryAfterS?: number;
  }) {
    super(input.mensagem);
    this.name = 'ListaVidasGatewayError';
    this.codigo = input.codigo;
    this.status = input.status;
    this.mensagem = input.mensagem;
    this.erros = input.erros;
    this.solicitacaoId = input.solicitacaoId;
    this.statusAtual = input.statusAtual;
    this.retentavel = input.retentavel;
    this.correlationId = input.correlationId;
    this.retryAfterS = input.retryAfterS;
  }
}

export interface IListaVidasGateway {
  meuPapel(): Promise<ListaVidasPapelInfo>;
  listarEmpresas(f?: { busca?: string; soCriadasPeloCrm?: boolean; limite?: number }): Promise<ListaVidasEmpresas>;
  listarVidas(i: { empresaPublicId: string }): Promise<ListaVidasVidas>;
  visaoGeral(i?: { incluirInativas?: boolean }): Promise<ListaVidasVisaoGeral>;
  obterVida(i: { empresaPublicId: string; seatPublicId: string }): Promise<{ vida: VidaCompletaDTO }>;
  listarSolicitacoes(f?: FiltroSolicitacoes): Promise<ListaVidasSolicitacoes>;
  obterSolicitacao(i: { id: string }): Promise<DetalheSolicitacao>;
  criar(i: NovaSolicitacao): Promise<{ solicitacao: SolicitacaoDTO; replay: boolean }>;
  aprovar(i: { id: string }): Promise<RespostaAplicacao>;
  retomar(i: { id: string }): Promise<RespostaAplicacao>;
  rejeitar(i: { id: string; motivo: string; aceitarIncerteza?: boolean }): Promise<{ solicitacao: SolicitacaoDTO }>;
  cancelar(i: { id: string }): Promise<{ solicitacao: SolicitacaoDTO }>;
  salvarAdicionalVida(i: SalvarAdicionalVidaInput): Promise<{ controle: ControleAdicionalDTO }>;
  liberarZenklub(i: LiberarZenklubInput): Promise<{ controle: ControleAdicionalDTO }>;
  salvarBonificacao(i: SalvarBonificacaoInput): Promise<{ bonificacao: BonificacaoEmpresa }>;
}
